import { useEffect, useState } from "react"
import { Alert, Button, Divider, Stack, Typography } from "@mui/material"
import { Bound } from "lib/@components/binding/Bound"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/switch"
import { Sortable } from "lib/@components/Sortable"
import { generate } from "library/guid/index.mjs"
import { ruleSets } from "routes/rule-sets/controller/rule-sets"
import { regimeConditionData } from "routes/rule-sets/controller/regime-condition-data"
import { assetRegisterConditionData } from "routes/rule-sets/controller/asset-register-condition-data"
import { RuleSetNameSection } from "../rule-sets/rule-set-name-section"
import { DefaultActionSelector } from "../rule-sets/default-action-selector"
import { RuleSetItem } from "../rule-sets/rule-set-item"
import { AddRuleButton } from "../rule-sets/add-rule-button"
import { AddRuleSetReferenceButton } from "../rule-sets/add-rule-set-reference-button"
import { ErrorAlert } from "../rule-sets/error-alert"
import { WarningAlert } from "../rule-sets/warning-alert"
import { DataContextSection } from "../rule-sets/data-context-section"
import { validateRuleSet } from "../rule-sets/validate-rule-set"
import { loadRuleSetContext, saveRuleSetContext } from "../rule-sets/rule-set-context-storage"
import { CONDITION_FIELDS } from "../rule-sets/condition-field-config"

export function EditRuleSetInScenarioDialog({ ok, cancel, ruleSet, regime }) {
    const storedContext = loadRuleSetContext(ruleSet?.id)
    const [name, setName] = useState(ruleSet?.name || "")
    const [description, setDescription] = useState(ruleSet?.description || "")
    const [justification, setJustification] = useState(ruleSet?.justification || "")
    const [defaultAction, setDefaultAction] = useState(ruleSet?.defaultAction || "include")
    const [rules, setRules] = useState(buildInitialRules)
    const [selectedRegime, setSelectedRegime] = useState(storedContext.regime || regime || null)
    const [selectedRegister, setSelectedRegister] = useState(storedContext.assetRegister || null)

    const regimeData = regimeConditionData.useResults(selectedRegime ? { regimeId: selectedRegime.id } : null)
    const registerData = assetRegisterConditionData.useResults(
        selectedRegister ? { registerId: selectedRegister.id } : null
    )
    const allRuleSets = ruleSets.useResults({ skip: 0, take: 500, status: "active" })
    const mergedRegimeData = mergeRegimeAndRegisterData()
    const validation = validateRuleSet({ name, description, defaultAction, rules })

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(resetFromRuleSet, [ruleSet?.id])

    return (
        <Bound
            conditionFields={CONDITION_FIELDS}
            regimeData={mergedRegimeData}
            availableRuleSets={allRuleSets?.items ?? []}
            currentRuleSetId={ruleSet?.id}
            rules={rules}
        >
            <Stack
                data-cy="edit-rule-set-in-scenario"
                spacing={3}
                sx={{ p: 3, maxHeight: "min(82vh, 880px)", overflow: "auto" }}
            >
                <Stack spacing={0.5}>
                    <Typography variant="h6">Edit Rule Set</Typography>
                    <Typography variant="caption" color="text.secondary">
                        Changes apply anywhere this reusable rule set is used.
                    </Typography>
                </Stack>
                <RuleSetNameSection
                    name={name}
                    description={description}
                    justification={justification}
                    onNameChange={setName}
                    onDescriptionChange={setDescription}
                    onJustificationChange={setJustification}
                />
                <Divider />
                <DataContextSection
                    selectedRegime={selectedRegime}
                    onRegimeChange={setSelectedRegime}
                    selectedRegister={selectedRegister}
                    onRegisterChange={setSelectedRegister}
                />
                <Divider />
                <DefaultActionSelector value={defaultAction} onChange={setDefaultAction} />
                <If value={validation.errors.length > 0}>
                    <Repeat list={validation.errors} item={<ErrorAlert />} />
                </If>
                <If value={validation.warnings.length > 0}>
                    <Repeat list={validation.warnings} item={<WarningAlert />} />
                </If>
                <Divider />
                <Stack spacing={2}>
                    <Stack spacing={0.5}>
                        <Typography variant="subtitle1" fontWeight={600}>
                            Rules
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Rules are evaluated in order. The first matching rule determines the action for each task.
                        </Typography>
                    </Stack>
                    <Sortable items={rules} id="id" onOrdered={handleReorder}>
                        <Repeat
                            list={rules}
                            item={
                                <RuleSetItem
                                    onUpdate={handleUpdateRule}
                                    onRemove={handleRemoveRule}
                                    onDuplicate={handleDuplicateRule}
                                />
                            }
                        />
                    </Sortable>
                    <Stack direction="row" spacing={1}>
                        <AddRuleButton onAdd={handleAddRule} />
                        <AddRuleSetReferenceButton onAdd={handleAddRule} />
                    </Stack>
                </Stack>
                <If value={!ruleSet?.id}>
                    <Alert severity="error">Rule set details could not be loaded.</Alert>
                </If>
                <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                    <Button onClick={cancel}>Cancel</Button>
                    <Button
                        data-cy="edit-rule-set-in-scenario-submit"
                        variant="contained"
                        disabled={validation.errors.length > 0 || !ruleSet?.id}
                        onClick={handleSubmit}
                    >
                        Save
                    </Button>
                </Stack>
            </Stack>
        </Bound>
    )

    function buildInitialRules() {
        return buildEditableRules(ruleSet?.rules)
    }

    function buildEditableRules(sourceRules) {
        return (sourceRules || []).map(toEditableRule)
    }

    function resetFromRuleSet() {
        if (!ruleSet?.id) return
        const context = loadRuleSetContext(ruleSet.id)
        setName(ruleSet.name || "")
        setDescription(ruleSet.description || "")
        setJustification(ruleSet.justification || "")
        setDefaultAction(ruleSet.defaultAction || "include")
        setRules(buildEditableRules(ruleSet.rules))
        setSelectedRegime(context.regime || regime || null)
        setSelectedRegister(context.assetRegister || null)
    }

    function toEditableRule(rule) {
        return {
            ...rule,
            type: rule.type || "rule",
            enabled: rule.enabled !== false,
            conditions: (rule.conditions || []).map(toEditableCondition),
            action: rule.action ? { ...rule.action } : { type: "include" },
        }
    }

    function toEditableCondition(condition) {
        return { _id: generate(), ...condition }
    }

    function mergeRegimeAndRegisterData() {
        if (!regimeData && !registerData) return null
        const base = regimeData || {
            facilities: [],
            towns: [],
            postcodes: [],
            assetTypes: [],
            regimeFields: [],
            assetRegisterFields: [],
        }
        if (!registerData?.assetRegisterFields?.length) return base
        return {
            ...base,
            assetRegisterFields: [...(base.assetRegisterFields || []), ...registerData.assetRegisterFields],
        }
    }

    function handleReorder(reordered) {
        setRules(reordered)
    }

    function handleAddRule(newRule) {
        setRules((previous) => [...previous, { ...newRule, enabled: true }])
    }

    function handleUpdateRule(ruleId, updatedRule) {
        setRules((previous) => previous.map((rule) => (rule.id === ruleId ? { ...rule, ...updatedRule } : rule)))
    }

    function handleRemoveRule(ruleId) {
        setRules((previous) => previous.filter((rule) => rule.id !== ruleId))
    }

    function handleDuplicateRule(rule) {
        const copy = {
            ...rule,
            id: generate(),
            conditions: (rule.conditions || []).map((condition) => ({ ...condition, _id: generate() })),
            action: rule.action ? { ...rule.action } : { type: "include" },
        }
        setRules((previous) => {
            const index = previous.findIndex(isRule(rule.id))
            const next = [...previous]
            next.splice(index + 1, 0, copy)
            return next
        })
    }

    function isRule(id) {
        return (item) => item.id === id
    }

    function handleSubmit() {
        saveRuleSetContext(ruleSet.id, {
            regime: selectedRegime || null,
            assetRegister: selectedRegister || null,
        })
        ok({
            name: name.trim(),
            description,
            justification,
            defaultAction,
            rules,
        })
    }
}
